// src/pages/Reports/BusinessReport.jsx

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const BusinessReport = ({ userType = 'super-distributor' }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    totalBet: 0,
    totalWin: 0,
    finalTotal: 0,
    commission: 0,
    netBalance: 0
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: ''
  });
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const getUserTypeLabel = () => {
    switch(userType) {
      case 'super-distributor': return 'Super Distributor';
      case 'distributor': return 'Distributor';
      case 'retailer': return 'Retailer';
      case 'user': return 'User';
      default: return 'Business';
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      let role = 'sub-admin';
      switch(userType) {
        case 'super-distributor': role = 'sub-admin'; break;
        case 'distributor': role = 'distributor'; break;
        case 'retailer': role = 'retailer'; break;
        case 'user': role = 'user'; break;
        default: role = '';
      }

      if (role) {
        const response = await api.get('/admin/users', {
          params: { role, limit: 100 }
        });
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [userType]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await api.get(`/admin/reports/${userType}`, { params });

      const data = response.data.reports || [];
      setReports(data);

      // ✅ SUMMARY CALCULATION
      const totalBet = data.reduce((sum, r) => sum + (r.totalBet || 0), 0);
      const totalWin = data.reduce((sum, r) => sum + (r.totalWin || 0), 0);
      const totalCommission = data.reduce((sum, r) => sum + (r.commission || 0), 0);
      const netBalance = data.reduce((sum, r) => sum + (r.netPay || 0), 0);

      setSummary({
        totalBet,
        totalWin,
        finalTotal: totalBet - totalWin,
        commission: totalCommission,
        netBalance
      });

      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 1
      }));

    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [userType, pagination.page, pagination.limit, filters]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchReports();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="busines-report-page">
      <div className="container-fluid py-4">

        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">
            {getUserTypeLabel()} Business Report
          </h1>
        </div>

        {/* SUMMARY */}
        <div className="row g-4 mb-5">
          <div className="col-lg-4 col-md-6">
            <div className="stat-card purple">
              <p>Total Bet</p>
              <h3>{formatAmount(summary.totalBet)}</h3>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="stat-card green">
              <p>Total Win</p>
              <h3>{formatAmount(summary.totalWin)}</h3>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="stat-card blue">
              <p>Final Total</p>
              <h3 className={summary.finalTotal < 0 }>
                {formatAmount(summary.finalTotal)}
              </h3>
            </div>
          </div>

          <div className="col-md-6">
            <div className="stat-card orange">
              <p>Commission</p>
              <h3>{formatAmount(summary.commission)}</h3>
            </div>
          </div>

          <div className="col-md-6">
            <div className="stat-card red">
              <p>Net Balance</p>
              <h3>{formatAmount(summary.netBalance)}</h3>
            </div>
          </div>
        </div>

        {/* REPORT */}
        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Report</h2>
          </div>

          <div className="admin-card-body">

            {/* FILTERS */}
            <div className="row g-3 pb-3 align-items-end">
              {/* <div className="col-lg-3 col-md-6">
                <select
                  name="userId"
                  value={filters.userId}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">All Users</option>
                  {users.map(u => (
                    <option key={u._id} value={u._id}>{u.username}</option>
                  ))}
                </select>
              </div> */}

              <div className="col-lg-3 col-md-6">
                <input type="date" name="startDate" onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-lg-3 col-md-6">
                <input type="date" name="endDate" onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-lg-3">
                <button className="btn btn-primary w-100" onClick={handleGenerateReport}>
                  Generate Report
                </button>
              </div>
            </div>

            {/* TABLE */}
            <div className="table-responsive">
              <table className="table table-admin-one">
                <thead className="theme-table-header">
                  <tr>
                    <th>Sr. No.</th>
                    <th>User</th>
                    <th>Total Bet</th>
                    <th>Total Win</th>
                    <th>End Point</th>
                    <th>Commission</th>
                    <th>Net Pay</th>
                  </tr>
                </thead>

                <tbody>
                  {reports.length > 0 ? (
                    reports.map((r, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{r.user}</td>
                        <td>{formatAmount(r.totalBet)}</td>
                        <td>{formatAmount(r.totalWin)}</td>
                        <td className={(r.totalBet-r.totalWin) < 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                          {formatAmount(r.totalBet-r.totalWin)}
                        </td>
                        <td>{formatAmount(r.commission)}</td>
                        <td>{formatAmount(r.netPay)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        No reports found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default BusinessReport;